import { useQuery } from 'convex/react';
import { api } from '../../api';
import { useUserDevice } from './useUserDevice';

interface AppCategory {
  name: string;
  tags: string[];
}

interface CategoryStatus {
  status: 'blocked' | 'allowed' | 'allowed_with_rules';
  isAllowed: boolean;
}

interface AppInfo {
  id: string;
  bundleId: string;
  name: string;
  iconUrl: string;
  category?: string;
}

const getCategoryStatus = (category: AppCategory, appFilter: any): CategoryStatus => {
  // appFilter === true means Open App Store mode (all categories allowed)
  if (appFilter === true) {
    return {
      status: 'allowed',
      isAllowed: true
    };
  }

  // appFilter === false or null/undefined means Approved Only mode (no categories)
  if (appFilter === false || appFilter === null || appFilter === undefined) {
    return {
      status: 'blocked',
      isAllowed: false
    };
  }

  // Check if category exists in appFilter object
  const categoryConfig = appFilter?.[category.name];
  
  if (categoryConfig === undefined) {
    // Category not found in filter - default to allowed
    return {
      status: 'allowed',
      isAllowed: true
    };
  }

  // Handle both simplified format (boolean) and full format (object with tagFilter)
  let tagFilter: boolean | object | undefined;
  if (typeof categoryConfig === 'boolean') {
    // Simplified format: { Category: false } or { Category: true }
    tagFilter = categoryConfig;
  } else if (typeof categoryConfig === 'object' && categoryConfig !== null) {
    // Full format: { Category: { source: "device", tagFilter: false } }
    tagFilter = categoryConfig.tagFilter;
  } else {
    tagFilter = undefined;
  }

  if (tagFilter === false) {
    // Explicitly blocked
    return {
      status: 'blocked',
      isAllowed: false
    };
  }

  if (tagFilter === true) {
    // Explicitly allowed
    return {
      status: 'allowed',
      isAllowed: true
    };
  }

  if (typeof tagFilter === 'object' && tagFilter !== null) {
    // Allowed with rules
    return {
      status: 'allowed_with_rules',
      isAllowed: true
    };
  }

  // Default fallback - blocked
  return {
    status: 'blocked',
    isAllowed: false
  };
};

export const useAppCategories = () => {
  const { device, isLoading: isDeviceLoading, currentUserIdentity, deviceConfig } = useUserDevice();
  
  // Check if user is a restricted identity (mentee/child)
  // Treat empty/unknown identity as restricted to avoid calling admin endpoints before identity resolves
  const identityLower = (currentUserIdentity ?? '').toLowerCase();
  const isRestrictedUser = ['mentee', 'child'].includes(identityLower) || !identityLower;

  // Fetch app categories from Convex - this is safe for all users
  const categories = useQuery(api.appCategories.list.categories);

  // Note: Mentees/children CAN now access getConfig per backend update
  const shouldSkipAppConfig = isDeviceLoading || !deviceConfig;
  
  const deviceAppConfig = useQuery(
    api.devices.apps.getConfig,
    !shouldSkipAppConfig ? { deviceConfig: deviceConfig as any } : "skip"
  );

  // Get allowed and blocked app IDs
  const allowedAppIds = deviceAppConfig?.allowedApps 
    ? Object.entries(deviceAppConfig.allowedApps)
        .filter(([_, value]) => value !== false && value !== null)
        .map(([appId]) => appId)
    : [];
    
  const blockedAppIds = deviceAppConfig?.blockedApps
    ? Object.entries(deviceAppConfig.blockedApps)
        .filter(([_, value]) => value !== false)
        .map(([appId]) => appId)
    : [];

  // Fetch app details
  const allowedAppsDetails = useQuery(
    api.apps.get.apps,
    allowedAppIds.length > 0 ? { apps: allowedAppIds as any } : "skip"
  );

  const blockedAppsDetails = useQuery(
    api.apps.get.apps,
    blockedAppIds.length > 0 ? { apps: blockedAppIds as any } : "skip"
  );

  // Combine categories with their device-specific status
  const categoriesWithStatus = categories?.map(category => {
    const categoryStatus = getCategoryStatus(category, deviceAppConfig?.appFilter);
    return {
      ...category,
      ...categoryStatus
    };
  }) || [];

  // Note: currentMode inference has been moved to useDeviceAppMode hook
  // Transform allowed apps to AppInfo format
  const allowedApps: AppInfo[] = (allowedAppsDetails || []).map((app, index) => ({
    id: allowedAppIds[index] || `app-${index}`,
    bundleId: app.bundleId,
    name: app.name,
    iconUrl: app.iconUrl,
    category: (app as any).category,
  }));

  // Transform blocked apps to AppInfo format
  const blockedApps: AppInfo[] = (blockedAppsDetails || []).map((app, index) => ({
    id: blockedAppIds[index] || `app-${index}`,
    bundleId: app.bundleId,
    name: app.name,
    iconUrl: app.iconUrl,
  }));

  // For restricted users, loading is only about categories (which are safe to fetch)
  const isLoading = isRestrictedUser 
    ? (categories === undefined)
    : (isDeviceLoading ||
       (categories === undefined) ||
       (deviceConfig !== null && deviceAppConfig === undefined) ||
       (allowedAppIds.length > 0 && allowedAppsDetails === undefined) ||
       (blockedAppIds.length > 0 && blockedAppsDetails === undefined));

  return {
    categories: categoriesWithStatus,
    isLoading,
    hasDevice: !!device,
    deviceAppConfig,
    allowedApps,
    blockedApps,
    isRestrictedUser,
    refetch: () => {
      // Convex handles refetching automatically
    },
  };
};
